import type {
  BaseServiceOptions,
  Pagination,
  TBaseCatalogs,
  TBaseFileCertificate,
  THistoryActions,
  TImportFile,
  TRecordAction,
  TRequestCatalog,
} from '@/core/types'
import {
  type TPurchaseOrder,
  moduleServiceHandlers,
  modulePrintHandlers,
  serviceConfig,
  type TPurchaseOrders,
  catalogServiceHandlers,
  TSupplier,
} from '../../..'
import { defaultPagination, useNotification, useServiceHandlers } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { useCatalogStore } from '@/core/store/catalogs'
import { toValue } from 'vue'
import { TMill } from '@/modules/mills'
const { model } = serviceConfig

export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, handlers, enableQuery, modelId, filters } = options
  const store = useCatalogStore()
  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TPurchaseOrder>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const getTotalPercentage = (total: number, quantity: number) => {
    if (!total) return 0

    const result = (quantity / total) * 100

    return result >= 100 ? 100 : Number(result.toFixed(2))
  }

  const editRecord = useMutation<TPurchaseOrder, AxiosError, TPurchaseOrder>(
    (payload: TPurchaseOrder) =>
      moduleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) =>
        serviceHandlers.updateRecord({
          ...payload,
          total_paid_percentage: getTotalPercentage(
            payload.total ?? 0,
            payload.total_paid ?? 0,
          ),
          total_received_percentage: getTotalPercentage(
            payload.total_ordered ?? 0,
            payload.total_received ?? 0,
          ),
        }),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TPurchaseOrder, AxiosError, TPurchaseOrder>(
    (payload: TPurchaseOrder) => moduleServiceHandlers.addRecord(payload),
    {
      onSuccess: (payload) =>
        serviceHandlers.addRecord({
          ...payload,
          total_paid_percentage: getTotalPercentage(
            payload.subtotal ?? 0,
            payload.total_paid ?? 0,
          ),
          total_received_percentage: getTotalPercentage(
            payload.total_ordered ?? 0,
            payload.total_received ?? 0,
          ),
        }),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const actionRecord = useMutation<TPurchaseOrder, AxiosError, TRecordAction>(
    (payload: TRecordAction) => moduleServiceHandlers.actionRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const importRecords = useMutation<void, AxiosError, TImportFile>(
    (payload: TImportFile) => {
      const formData = new FormData()
      formData.append('importFile', payload.file)
      return moduleServiceHandlers.importRecords(formData)
    },
    {
      onSuccess: () => serviceHandlers.invalidateQueries(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const uploadCertificate = useMutation<
    TPurchaseOrder & TBaseFileCertificate,
    AxiosError,
    TPurchaseOrder
  >(
    (payload: TPurchaseOrder & TBaseFileCertificate) =>
      moduleServiceHandlers.uploadCertificate(
        modelToFormData(payload, 'PUT'),
        payload.id,
      ),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TPurchaseOrders>(
    [model, currentPage, filters?.loadRecords],
    () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecords,
      onSuccess: (data) => {
        if (data.meta) {
          handlers?.onDataChange?.(data.meta ?? defaultPagination)
        }
      },
      select: (data) => ({
        ...data,
        data: data.data.map((record) => ({
          ...record,
          total_paid_percentage: getTotalPercentage(
            record.total ?? 0,
            record.total_paid ?? 0,
          ),
          total_received_percentage: getTotalPercentage(
            record.total_ordered ?? 0,
            record.total_received ?? 0,
          ),
        })),
      }),
    },
  )

  const loadRecord = useQuery<TPurchaseOrder, AxiosError>({
    queryKey: [model, modelId],
    queryFn: ({ queryKey }) =>
      moduleServiceHandlers.loadRecord({
        id: Number(queryKey[1]),
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecord,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data.id) {
        handlers?.onLoadRecord?.(data)
      }
    },
    select: (data) => ({
      ...data,
      tax_rate: Number(data.tax_rate),
      total_paid_percentage: getTotalPercentage(
        data.total ?? 0,
        data.total_paid ?? 0,
      ),
      total_received_percentage: getTotalPercentage(
        data.total_ordered ?? 0,
        data.total_received ?? 0,
      ),
    }),
  })

  const requestCatalog = useMutation<
    TBaseCatalogs,
    AxiosError,
    TRequestCatalog
  >(
    (payload: Record<any, any>) =>
      catalogServiceHandlers.categoriesCatalog.requestCatalog(payload),
    {
      onSuccess: (payload) => {
        store.setCatalogs(payload)
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  const suppliersCatalog = useQuery<TSupplier[]>(
    [serviceConfig.catalogs.suppliers, filters?.suppliers],
    () =>
      catalogServiceHandlers.suppliersCatalog.loadCatalog({
        dynamicFilters: filters?.suppliers,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.suppliersCatalog,
    },
  )

  const millsCatalog = useQuery<TMill[], AxiosError>({
    queryKey: [serviceConfig.catalogs.mills, filters?.millsCatalog],
    queryFn: () =>
      catalogServiceHandlers.millsCatalog.loadCatalog({
        dynamicFilters: filters?.millsCatalog,
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.millsCatalog,
  })

  const loadHistoryActions = useQuery<THistoryActions[], AxiosError>({
    queryKey: ['history-actions', filters?.loadHistoryActions, modelId],
    queryFn: () => {
      return moduleServiceHandlers.loadHistoryActions({
        id: toValue(modelId),
      })
    },
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadHistoryActions,
    onError: (error) => errorHttpNotification(error),
  })

  const printRecord = async (id: number) =>
    await modulePrintHandlers.printRecord(id)

  const modelToFormData = (
    payload: TPurchaseOrder & TBaseFileCertificate,
    method?: string,
  ) => {
    const formData = new FormData()
    if (payload?.file) {
      formData.append('file', payload.file)
    }

    if (method) {
      formData.append('_method', method)
    }

    return formData
  }

  const deleteRecord = useMutation<void, AxiosError, TPurchaseOrder>(
    (payload) => moduleServiceHandlers.deleteRecord(payload),
    {
      onSuccess: (_, warehouse) => serviceHandlers.deleteRecord(warehouse),
      onError: (error) => errorHttpNotification(error),
    },
  )

  return {
    serviceHandlers,
    suppliersCatalog,
    requestCatalog,
    loadRecord,
    loadRecords,
    addRecord,
    editRecord,
    importRecords,
    actionRecord,
    loadHistoryActions,
    uploadCertificate,
    millsCatalog,
    deleteRecord,
    printRecord,
  }
}
